import axios from 'axios'
import { FETCH_ROLES, FETCH_ADMINS, FETCH_ENTITLEMENTS, CLEAR } from './Entitlements.mutation-types'
import { apiErrorHandler } from '@/core/helpers/api.helpers'
import { allSettled } from '@/core/helpers/promise.helpers'

/**
 *  Boot entitlements
 *
 * @param dispatch
 * @param rootGetters
 *
 * @returns {Promise<any>}
 */
export const boot = ({ dispatch, rootGetters }) => {
  return new Promise((resolve) => {
    const boot = rootGetters['auth/entitlements'].getAnyPassed({
      'fetchRoles': { admin_role: 'read' },
      'fetchAdmins': { admin: 'read' },
      'fetchEntitlements': { admin_role: 'read' }
    }).map(action => dispatch(action)).map(allSettled)

    return Promise.all(boot).then(responses => resolve(responses))
  })
}

/**
 *  Fetch roles
 *
 * @param commit
 *
 * @returns {Promise<any>}
 */
export const fetchRoles = ({ commit, rootGetters }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ admin_role: 'read' }, { throwError: true })

    axios.get('/api/v1/admin_roles')
      .then(({ data }) => {
        commit(FETCH_ROLES, data.roles)
        resolve(data)
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Create role
 *
 * @param dispatch
 * @param rootGetters
 *
 * @returns {Promise<any>}
 */
export const createRole = ({ dispatch, rootGetters }, { name, entitlements }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ admin_role: 'write' }, { throwError: true })

    axios.post('/api/v1/admin_roles', { name, entitlements })
      .then(({ data }) => {
        dispatch('fetchRoles')
          .then(() => {
            resolve(data)
          })
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Update role
 *
 * @param commit
 * @param rootGetters
 *
 * @returns {Promise<any>}
 */
export const updateRole = ({ dispatch, rootGetters }, { id, name, entitlements }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ admin_role: 'write' }, { throwError: true })

    axios.put(`/api/v1/admin_roles/${id}`, { name, entitlements })
      .then(({ data }) => {
        dispatch('fetchRoles')
          .then(() => {
            resolve(data)
          })
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Delete role
 *
 * @param dispatch
 * @param rootGetters
 *
 * @returns {Promise<any>}
 */
export const deleteRole = ({ dispatch, rootGetters }, { id }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ admin_role: 'delete' }, { throwError: true })

    axios.delete(`/api/v1/admin_roles/${id}`)
      .then(({ data }) => {
        dispatch('fetchRoles')
          .then(() => {
            resolve(data)
          })
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Fetch entitlements roles
 *
 * @param commit
 *
 * @returns {Promise<any>}
 */
export const fetchAdmins = ({ commit, rootGetters }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ admin: 'read' }, { throwError: true })

    axios.get('/api/v1/admins')
      .then(({ data }) => {
        commit(FETCH_ADMINS, data.admins)
        resolve(data)
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Create admin
 *
 * @param dispatch
 * @param rootGetters
 * @param payload
 *
 * @returns {Promise<any>}
 */
export const createAdmin = ({ dispatch, rootGetters }, payload) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ admin: 'write' }, { throwError: true })

    axios.post('/api/v1/admins', payload)
      .then(({ data }) => {
        dispatch('fetchAdmins')
          .then(() => {
            resolve(data)
          })
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Update admin
 *
 * @param dispatch
 * @param rootGetters
 * @param id
 * @param payload
 *
 * @returns {Promise<any>}
 */
export const updateAdmin = ({ dispatch, rootGetters }, payload) => {
  rootGetters['auth/entitlements'].can({ admin: 'write' }, { throwError: true })

  const id = payload.id
  delete payload['id']

  return new Promise((resolve, reject) => {
    axios.put(`/api/v1/admins/${id}`, payload)
      .then(({ data }) => {
        dispatch('fetchAdmins')
          .then(() => {
            resolve(data)
          })
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Delete Admin
 *
 * @param dispatch
 * @param rootGetters
 * @param id
 *
 * @returns {Promise<any>}
 */
export const deleteAdmin = ({ dispatch, rootGetters }, { id }) => {
  rootGetters['auth/entitlements'].can({ admin: 'delete' }, { throwError: true })

  return new Promise((resolve, reject) => {
    axios.delete(`/api/v1/admins/${id}`)
      .then(({ data }) => {
        dispatch('fetchAdmins')
          .then(() => {
            resolve(data)
          })
      })
      .catch(error => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 *  Fetch entitlements roles
 *
 * @param commit
 * @param state
 *
 * @returns {Promise<any>}
 */
export const fetchEntitlements = ({ commit }) => {
  return new Promise((resolve) => {
    const data = {
      entitlements: {
        admin: [ 'read', 'write', 'delete' ],
        admin_role: [ 'read', 'write' ],
        assessment: [ 'read', 'write', 'debug' ],
        auth: [ 'unlock' ],
        company: [ 'read', 'switch', 'write' ],
        location: [ 'read', 'write' ],
        schedule: [ 'read', 'write' ],
        score: [ 'read', 'write' ],
        task: [ 'read', 'write' ],
        task_type: [ 'read', 'write' ],
        telemetry: [ 'add', 'read', 'write' ],
        user_type: [ 'read', 'write' ]
      }
    }

    commit(FETCH_ENTITLEMENTS, data.entitlements)
    resolve(data)
  })
}

/**
 * Reset stored data
 *
 * @param commit
 */
export const clear = ({ commit }) => {
  commit(CLEAR)
}

export default {
  boot,
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  fetchAdmins,
  createAdmin,
  updateAdmin,
  deleteAdmin,
  fetchEntitlements,
  clear
}
